<style lang="scss">
.record-details-modal {
  .modal-dialog {
    max-width: 90vw;
    margin: 1.75rem auto;
  }
  .modal-body {
    max-height: 75vh;
    overflow-y: auto;
  }
}

.modal .visitor-image-large {
  max-height: 50vh;
  width: auto;
  display: block;
  margin: 0 auto;
}
.prisoner-verification-records-page {
  .column-actions {
    width: 1%;
    white-space: nowrap;
  }
}
</style>

<template>
  <div class="theme-color-wrapper p-2 prisoner-verification-records-page">
    <div class="records row">
      <div class="col-12">
        <h2 cy="page-title" class="font-weight-bolder mb-3">{{ 'prisoner' | term(true) }} Verification Records</h2>

        <section class="my-2 mb-3 d-flex flex-row">
          <b-form-input
            v-model="search"
            :placeholder="`Search by ${$options.filters.term('prisoner', true)} Number...`"
            cy="prisoner-verification-search"
            class="col-6"
            @input="getRecords"
          />
          <b-form-select
            v-model="selectedStatus"
            :options="statusOptions"
            class="col-3 ml-2"
            @change="getRecords"
          />
        </section>

        <b-card no-body class="mb-0">
          <section>
            <b-table
              class="data-table"
              cy="prisoner-verification-table"
              hover
              :items="paginatedRecords"
              :fields="headers"
              :busy="loading"
              :responsive="true"
              @row-clicked="viewRecordDetails"
            >
              <template #cell(verification_status)="data">
                <b-badge :variant="getStatusVariant(data.item.verification_status)">
                  {{ data.item.verification_status }}
                </b-badge>
              </template>
              <template #cell(updated_at)="data">
                {{ formatDate(data.item.updated_at) }}
              </template>
            </b-table>

            <b-row>
              <b-col v-if="records.length > 0" md="6" class="my-1 pl-2">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRecords"
                  :per-page="perPage"
                  class="my-0"
                />
              </b-col>
              <p v-if="records.length === 0" class="col-6 ml-2 my-1">
                {{ loading ? "Loading...." : `No ${$options.filters.term('prisoner', false)} verification records to show` }}
              </p>
            </b-row>
          </section>
        </b-card>
      </div>
    </div>

    <b-modal id="rejection-modal" hide-footer>
      <warning-modal
        :show-text-field="true"
        ok-variant="danger"
        title="Reject Verification (Any in-room bookings will be cancelled)"
        note="NOTE: This reason will be visible to the prisoner."
        @close="closeRejectModal"
        @ok="handleRejectConfirmation"
      />
    </b-modal>
    <b-modal id="record-details-modal" size="lg" scrollable title="Record Details">
      <div v-if="selectedRecord" class="d-flex justify-content-center mb-3">
        <images-side-by-side-modal v-if="selectedRecord.selfie_images && selectedRecord.selfie_images.length" :image-urls="selectedRecord.selfie_images.map(img => img.url)" />
        <p v-else class="text-muted">No images available for this verification.</p>
      </div>

      <div v-if="selectedRecord" class="record-details px-3">
        <h5>Verification Status: <b-badge :variant="getStatusVariant(selectedRecord.verification_status)">{{ selectedRecord.verification_status }}</b-badge></h5>
        <p v-if="selectedRecord.reviewer && selectedRecord.reviewer.name"><strong>Reviewed By:</strong> {{selectedRecord.reviewer.name}}</p>
        <p v-if="selectedRecord.verification_status === 'Rejected' && selectedRecord.rejection_reason"><strong>Rejection Reason:</strong> {{ selectedRecord.rejection_reason }}</p>
        <p v-if="selectedRecord.prisoner_number"><strong>Prisoner Number:</strong> {{ selectedRecord.prisoner_number }}</p>
        <p v-if="selectedRecord.updated_at"><strong>Updated At:</strong> {{ formatDate(selectedRecord.updated_at) }}</p>
      </div>
      <div v-else class="d-flex justify-content-center">
        <p class="text-muted">Loading verification record details...</p>
      </div>

      <template #modal-footer>
        <div class="d-flex justify-content-end w-100">
          <b-button
            class="mr-1"
            v-show="selectedRecord && selectedRecord.verification_status !== VERIFICATION_STATUS.REJECTED"
            variant="danger"
            :disabled="processingAction"
            @click="openRejectModal(selectedRecord._id)"
          >
            Reject
          </b-button>
          <b-button
            v-show="selectedRecord && selectedRecord.verification_status !== VERIFICATION_STATUS.VERIFIED"
            class="ms-2"
            variant="success"
            :disabled="processingAction"
            @click="allowRecord(selectedRecord._id)"
          >
            Approve
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import PrisonerVerificationService from '@/services/PrisonerVerificationService';
import {
  BButton, BCard, BCol, BFormInput, BPagination, BRow, BTable, BModal, BFormSelect,
} from 'bootstrap-vue';
import WarningModal from '@/components/modals/WarningModal.vue';
import ImagesSideBySideModal from '@/components/modals/ImagesSideBySideModal.vue';
import { VERIFICATION_STATUS } from '@/constants/status';
import moment from "moment";

export default {
  name: 'PrisonerVerificationRecords',
  components: {
    BButton,
    BFormInput,
    BCard,
    BTable,
    BRow,
    BCol,
    BPagination,
    WarningModal,
    BModal,
    ImagesSideBySideModal,
    BFormSelect,
  },
  data() {
    return {
      search: '',
      loading: false,
      records: [],
      totalRecords: 0,
      currentPage: 1,
      perPage: 10,
      headers: [
        { label: 'Prisoner Number', key: 'prisoner_number' },
        { label: 'Status', key: 'verification_status' },
        // TODO: Format this on the table, see how we do it elsewhere
        { label: 'Updated At', key: 'updated_at' },
      ],
      recordToRejectId: null,
      selectedRecord: null,
      processingAction: false,
      selectedStatus: 'AwaitingVerification',
      VERIFICATION_STATUS,
    };
  },
  computed: {
    paginatedRecords() {
      const startRow = (this.currentPage - 1) * this.perPage;
      const endRow = this.currentPage * this.perPage;
      return this.records.slice(startRow, endRow);
    },
    statusOptions() {
      const options = [{ value: '', text: 'All' }];
      options.push({ value: VERIFICATION_STATUS.AWAITING_VERIFICATION, text: 'Awaiting Verification' });
      options.push({ value: VERIFICATION_STATUS.VERIFIED, text: 'Verified' });
      options.push({ value: VERIFICATION_STATUS.REJECTED, text: 'Rejected' });
      return options;
    },
  },
  mounted() {
    this.getRecords();
  },
  methods: {
    formatDate(value) {
      if (!value) return '';
      return moment(value).local().format('DD/MM/YYYY HH:mm:ss z');
    },
    getRecords() {
      this.loading = true;
      const params = {};
      if (this.selectedStatus !== '') {
        params.status = this.selectedStatus;
      }
      if (this.search && this.search.trim() !== '') {
        params.prisonerNumber = this.search.trim();
      }
      PrisonerVerificationService.getVerificationRecordsPaginated(params)
        .then(res => {
          this.records = res.data || [];
          this.totalRecords = res.count || 0;
          this.currentPage = 1;
        })
        .catch(err => {
          console.error('Error fetching resident verification records:', err);
          this.$toast.error('Could not fetch verification records, please refresh and try again', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    allowRecord(recordId) {
      if (this.processingAction) return;
      this.processingAction = true;
      const record = this.records.find(r => r._id === recordId);
      const prisonerNumber = record ? record.prisoner_number : 'N/A';

      this.$swal({
        title: 'Confirm Likeness',
        text: `Is this a true likeness of ${prisonerNumber}?`,
        icon: 'info',
        buttons: ['No', 'Yes'],
      }).then(result => {
        if (result) {
          PrisonerVerificationService.approveVerificationRecord(recordId)
            .then(() => {
              this.$toast.success('Verification approved!', {
                toastClassName: ['toast-std', 'success-toast'],
              });
              this.getRecords();
              this.$bvModal.hide('record-details-modal');
            })
            .catch(err => {
              console.error(`Error allowing record ${recordId}:`, err);
              this.$toast.error('Failed to verify. Please try again.', {
                toastClassName: ['toast-std', 'warning-toast'],
              });
            })
            .finally(() => {
              this.processingAction = false;
            });
        }
      })
      .finally(() => {
        this.processingAction = false;
      });
    },
    openRejectModal(recordId) {
      this.recordToRejectId = recordId;
      this.$bvModal.show('rejection-modal');
    },
    closeRejectModal() {
      this.recordToRejectId = null;
      this.$bvModal.hide('rejection-modal');
    },
    handleRejectConfirmation(reason) {
      if (this.processingAction) return;
      if (!reason) {
        this.$toast.warning('Rejection reason is required.', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        return;
      }
      if (this.recordToRejectId) {
        this.processingAction = true;
        PrisonerVerificationService.rejectVerificationRecord(this.recordToRejectId, reason)
          .then(() => {
            this.$toast.success('Verification rejected!', {
              toastClassName: ['toast-std', 'success-toast'],
            });
            this.getRecords();
            this.$bvModal.hide('record-details-modal');
            this.closeRejectModal();
          })
          .catch(err => {
            console.error(`Error rejecting record ${this.recordToRejectId}:`, err);
            this.$toast.error('Failed to reject verification. Please try again.', {
              toastClassName: ['toast-std', 'warning-toast'],
            });
          })
          .finally(() => {
            this.processingAction = false;
          });
      }
    },
    viewRecordDetails(record) {
      this.selectedRecord = record;
      this.$bvModal.show('record-details-modal');
    },
    getStatusVariant(status) {
      switch (status) {
        case VERIFICATION_STATUS.VERIFIED:
          return 'success';
        case VERIFICATION_STATUS.REJECTED:
          return 'danger';
        case VERIFICATION_STATUS.AWAITING_VERIFICATION:
          return 'warning';
        default:
          return 'secondary';
      }
    },
  },
};
</script>
